import React from "react";
import { FEResponse } from "@/types/parts";
import { AlertCircle, Wrench, Package, Loader2, ClipboardList, Copy, Database, Code } from "lucide-react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UITable,
} from "@/components/ui/table";
import { toast } from "sonner";
import "./FEDisplay.css";

interface FEDisplayProps {
  feResponse: FEResponse;
  isLoading?: boolean;
  error?: string;
}

const FEDisplay: React.FC<FEDisplayProps> = ({
  feResponse,
  isLoading = false,
  error,
}) => {
  const components = feResponse?.components || [];
  const repairs = feResponse?.repairs || [];
  const repairJobs = feResponse?.repair_jobs || [];
  
  const hasAnyResults = components.length > 0 || repairs.length > 0 || repairJobs.length > 0;

  const handleCopyId = async (id: string, type: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success(`${type} copied to clipboard`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="parts-display-loading">
        <div className="loading-header">
          <Loader2 size={20} className="animate-spin" />
          <span>Extracting features...</span>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="parts-display-error">
        <AlertCircle size={24} className="error-icon" />
        <h3>Unable to Extract Features</h3>
        <p>{error}</p>
      </div>
    );
  }

  // Empty State
  if (!hasAnyResults) {
    return (
      <div className="parts-display-empty">
        <Package size={48} className="empty-icon" />
        <h3>No Features Found</h3>
        <p>Try adjusting your search terms or selecting different feature types.</p>
      </div>
    );
  }

  return (
    <div className="modern-parts-display professional">
      <div className="professional-table-container">
        {/* Components Table */}
        {components.length > 0 && (
          <div className="table-section">
            <div className="section-header components">
              <div className="header-icon">
                <Package size={16} />
              </div>
              <div className="header-text">
                <h3>Components</h3>
                <span className="part-count">{components.length} components</span>
              </div>
            </div>

            <UITable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-64">Extracted Component (NPT)</TableHead>
                  <TableHead className="w-48">Part Terminology ID</TableHead>
                  <TableHead className="w-64">Part Terminology Name</TableHead>
                  <TableHead className="w-40">Category</TableHead>
                  <TableHead className="w-40">Sub-Category</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {components.map((component, index) => (
                  <TableRow key={`${component.part_terminology_id}-${index}`}>
                    <TableCell>
                      <span className="extracted-term">{component.npt}</span>
                    </TableCell>
                    <TableCell>
                      <div className="table-id-cell fe">
                        <Database size={12} />
                        <code>{component.part_terminology_id}</code>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {component.part_terminology_name}
                    </TableCell>
                    <TableCell>{component.category}</TableCell>
                    <TableCell>
                      {component.sub_category || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        className="table-copy-btn"
                        onClick={() => handleCopyId(component.part_terminology_id.toString(), "Part Terminology ID")}
                        aria-label="Copy part terminology ID"
                      >
                        <Copy size={12} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </UITable>
          </div>
        )}

        {/* Repairs Table */}
        {repairs.length > 0 && (
          <div className="table-section">
            <div className="section-header repairs">
              <div className="header-icon">
                <Wrench size={16} />
              </div>
              <div className="header-text">
                <h3>Repairs</h3>
                <span className="part-count">{repairs.length} repairs</span>
              </div>
            </div>

            <UITable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-96">Extracted Repair (NPT)</TableHead>
                  <TableHead className="w-96">Predii PT</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repairs.map((repair, index) => (
                  <TableRow key={`repair-${index}`}>
                    <TableCell>
                      <span className="extracted-term">{repair.npt}</span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {repair.pt}
                    </TableCell>
                    <TableCell>
                      <button
                        className="table-copy-btn"
                        onClick={() => handleCopyId(repair.pt, "Predii PT")}
                        aria-label="Copy Predii PT"
                      >
                        <Copy size={12} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </UITable>
          </div>
        )}

        {/* Repair Jobs Table */}
        {repairJobs.length > 0 && (
          <div className="table-section">
            <div className="section-header repair-jobs">
              <div className="header-icon">
                <ClipboardList size={16} />
              </div>
              <div className="header-text">
                <h3>Repair Jobs</h3>
                <span className="part-count">{repairJobs.length} repair jobs</span>
              </div>
            </div>

            <UITable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-96">Extracted Repair Job (NPT)</TableHead>
                  <TableHead className="w-96">Predii PT</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repairJobs.map((job, index) => (
                  <TableRow key={`job-${index}`}>
                    <TableCell>
                      <span className="extracted-term">{job.npt}</span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {job.pt}
                    </TableCell>
                    <TableCell>
                      <button
                        className="table-copy-btn"
                        onClick={() => handleCopyId(job.pt, "Predii PT")}
                        aria-label="Copy Predii PT"
                      >
                        <Copy size={12} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </UITable>
          </div>
        )}
      </div>
    </div>
  );
};

export default FEDisplay;
